<template>
  <Toast />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <ModalTable v-if="modalShowTable" @closeModal="closeModal" :table="selectedTable" />
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ hidden: !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Barang Keluar" />
      <div class="bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg">
        <div id="table-header" class="mb-5 flex justify-between">
          <AddButton to_url="#" button_text="+ Tambah Barang Keluar" button_class="text-base py-2 font-medium"
            @click="showModal" />
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow-lg rounded-lg">
              <TableComponent>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>No</TableHeadCell>
                    <TableHeadCell>Kode</TableHeadCell>
                    <TableHeadCell>Tanggal</TableHeadCell>
                    <TableHeadCell>Status</TableHeadCell>
                    <TableHeadCell>Action</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow v-if="allOutboundItemsForms.length == 0">
                    <TableCell colspan="5" customClass="text-center italic text-gray-400 dark:text-gray-500"
                      noTextColor>
                      Belum ada data
                    </TableCell>
                  </TableRow>
                  <TableRow v-else v-for="(outboundItemsForm, index) in allOutboundItemsForms" :key="outboundItemsForm.id" :index="index" isBody>
                    <TableCell>{{ index + 1 }}</TableCell>
                    <TableCell>{{ outboundItemsForm.code }}</TableCell>
                    <TableCell>{{ outboundItemsForm.createdAt }}</TableCell>
                    <TableCell>
                      <span :class="{
                        'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300': outboundItemsForm.status === 'paid',
                        'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300': outboundItemsForm.status === 'pending',
                        'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-300': outboundItemsForm.status === 'canceled'
                      }" class="px-2 py-1 rounded-full text-sm font-medium">
                        {{ getStatusText(outboundItemsForm.status) }}
                      </span>
                    </TableCell>
                    <TableCell noTextColor customClass="text-gray-500 dark:text-gray-400 flex">
                      <button :id="'edit-data-' + outboundItemsForm.id" @click="editOutboundItemsForm(outboundItemsForm)"
                        class="border border-gray-400 hover:border-green-700 hover:bg-green-400 flex justify-center items-center mx-1 p-2 rounded-md group">
                        <PencilSquareIcon class="h-4 w-4 group-hover:text-green-700" />
                      </button>
                      <template v-if="outboundItemsForm.status != 'paid'">
                        <button :id="'delete-data-' + outboundItemsForm.id" @click="deleteOutboundItemsForm(outboundItemsForm)"
                          class="border border-gray-400 hover:border-red-500 hover:bg-red-400 flex justify-center items-center mx-1 p-2 rounded-md group">
                          <TrashIcon class="h-4 w-4 group-hover:text-red-700" />
                        </button>
                      </template>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </TableComponent>
              <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
import IndexView from "@/views/IndexView.vue";
import PageTitle from "@/components/PageTitle.vue";
import AddButton from "@/components/buttons/AddButton.vue";
import ModalTable from "@/components/modals/ModalTable.vue";
import ViewContainer from "@/components/ViewContainer.vue";
import {
  TableComponent,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableCell,
} from "@/components/table";
import { PencilSquareIcon, TrashIcon } from "@heroicons/vue/24/solid";
import { mapGetters, mapActions } from "vuex";
import PaginationButtons from "@/components/PaginationButtons.vue";
import { Button, ConfirmDialog, Toast } from "primevue";

export default {
  name: "OutboundItemsFormView",
  components: {
    IndexView, PageTitle, AddButton, ModalTable, ViewContainer, TableComponent, TableHead, TableRow, TableHeadCell, TableBody, TableCell, PencilSquareIcon, TrashIcon, PaginationButtons, Toast, ConfirmDialog, Button
  },
  data() {
    return {
      modalShowTable: false,
      isBlur: false,
      selectedTable: null,
      currentPage: 1,
      totalPages: 1,
    }
  },
  computed: {
    ...mapGetters('outbound_items_forms', ['allOutboundItemsForms'])
  },
  created() {
    this.fetchOutboundItemsForms();
  },
  watch: {
    currentPage: {
      immediate: true,
      handler(newVal) {
        this.fetchOutboundItemsForms({ page: newVal });
      },
    }
  },
  methods: {
    ...mapActions('outbound_items_forms', ['deleteOutboundItemsFormById']),
    async fetchOutboundItemsForms(params = {}) {
      if (!params.page) params.page = this.currentPage;

      try {
        const { pagination } = await this.$store.dispatch('outbound_items_forms/fetchOutboundItemsForms', params);
        if (pagination) {
          this.totalPages = pagination.totalPages;
        }
      } catch (error) {
        console.log(error);
      }
    },
    getStatusText(status) {
      const statusMap = {
        'paid': 'Posted',
        'pending': 'Draft',
        'canceled': 'Void'
      };
      return statusMap[status] || status;
    },
    showModal() {
      this.$router.push('/outbound-items-form/new');
      // this.modalShowTable = true;
      // this.isBlur = true;
      // this.selectedTable = null;
    },
    closeModal() {
      setTimeout(() => {
        this.modalShowTable = false;
        this.isBlur = false;
        this.fetchTables();
      }, 200);
    },
    editOutboundItemsForm(data) {
      this.$router.push(`outbound-items-form/edit/${data.id}`);
    },
    async deleteOutboundItemsForm(table) {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin menghapus barang keluar?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            await this.deleteOutboundItemsFormById(table.id);
            this.fetchOutboundItemsForms();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil menghapus barang keluar', life: 3000 });
          }
          catch (error) {
            console.error('Gagal menghapus barang keluar', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal menghapus barang keluar', life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal menghapus barang keluar', life: 3000 });
        }
      });
    }
  },
}
</script>
